/**
 * SuggestionsDialog — painel de buscas sugeridas (porta de `showSuggestions`
 * [application.js:9145]). #window > .context.suggestions > close + h2 smart_assistant + p +
 * ul (uma opção por sugestão) + botão "mais sugestões". Diferença do legado: em vez de
 * enviar os parâmetros para o popup da extensão (iframe), o app2 PRÉ-PREENCHE o formulário
 * de captura (parseSuggestion → captureFormMemory) e abre a janela de captura do módulo.
 *
 * Movimento (23/10/2026, guia/10 "Janela e diálogo"): o #window é o véu (.ds-veu) e o .context
 * é a janela (.ds-dialogo), os dois com .is-aberto. Véu e janela entram em 200 ms (a janela
 * sobe 8 px e vai de 0,97 a 1) e saem em 140 ms, pelo caminho inverso; abrir e fechar rápido
 * volta do meio. Com "reduzir movimento", só fade de 120 ms. Ao abrir, o foco vai ao título
 * (quando a janela fica visível); Esc fecha; ao fechar, o foco volta a quem abriu assim que a
 * janela começa a sair (não espera os 140 ms, em que ela fica invisível).
 */
import { useEffect, useRef } from 'react';
import { LoadingStatus, Skeleton } from '../../components/common/Skeleton';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSearchSuggestions, type SearchSuggestion } from '../../api/useSuggestions';
import { useAssistantStore } from './assistantStore';
import { useCaptureStore } from '../../stores/captureStore';
import { parseSuggestion } from '../../lib/parseSuggestion';
import { saveCaptureForm } from '../../lib/captureFormMemory';
import { usePresenca } from '../../hooks/usePresenca';

export function SuggestionsDialog(): JSX.Element | null {
  const { t } = useTranslation();
  const open = useAssistantStore((s) => s.suggestionsOpen);
  const closeSuggestions = useAssistantStore((s) => s.closeSuggestions);
  const openCaptureWindow = useCaptureStore((s) => s.openCaptureWindow);
  const query = useSearchSuggestions(open);
  // Só as sugestões que abrem um módulo do painel (as do Hunter ficam de fora).
  const suggestions = query.data?.filter((s) => parseSuggestion(s.parameters) !== null);
  // Fica montada enquanto sai (140 ms, o degrau de baixo dos 200 ms da entrada).
  const presenca = usePresenca(open, '--duracao-140');
  const aberto = presenca.aberto ? ' is-aberto' : '';
  const janelaRef = useRef<HTMLDivElement | null>(null);
  const tituloRef = useRef<HTMLHeadingElement | null>(null);
  // Quem tinha o foco quando a janela abriu (o aviso do assistente).
  const quemAbriuRef = useRef<HTMLElement | null>(null);

  // Abriu: guarda quem abriu, antes do foco entrar na janela. Fechou: o foco volta a ele na
  // hora, porque a janela que sai fica invisível e o foco nela se perderia. Só se o foco ainda
  // estiver na janela (ou perdido): escolher uma sugestão abre a janela de captura, que leva o
  // foco para ela.
  useEffect(() => {
    if (open) {
      quemAbriuRef.current = document.activeElement as HTMLElement | null;
      return;
    }
    const quemAbriu = quemAbriuRef.current;
    quemAbriuRef.current = null;
    const ativo = document.activeElement;
    const focoNaJanela =
      !ativo || ativo === document.body || Boolean(janelaRef.current?.contains(ativo));
    if (quemAbriu?.isConnected && focoNaJanela) quemAbriu.focus();
  }, [open]);

  // A janela nasce fechada (.ds-dialogo invisível) e ganha .is-aberto um quadro depois: o foco
  // vai ao título quando ela fica visível, se ainda não estiver dentro dela.
  useEffect(() => {
    if (presenca.aberto && !janelaRef.current?.contains(document.activeElement)) {
      tituloRef.current?.focus();
    }
  }, [presenca.aberto]);

  // Esc fecha enquanto ela está aberta.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeSuggestions();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeSuggestions]);

  if (!presenca.montado) return null;

  function apply(suggestion: SearchSuggestion): void {
    const form = parseSuggestion(suggestion.parameters);
    if (!form) return;
    // Pré-preenche a memória do módulo e abre a captura já com os campos. country/areaCode
    // são ComboOption no form; a sugestão traz só o código — reidratamos como {value,text}.
    saveCaptureForm(form.module, {
      keywords: form.keywords,
      sites: form.sites,
      domains: form.domains,
      places: form.places,
      areaCode: form.areaCode ? { value: form.areaCode, text: form.areaCode } : undefined,
    });
    closeSuggestions();
    openCaptureWindow(form.module);
  }

  return createPortal(
    <div
      id="window"
      // O .show do legado (opacidade 1 com !important) saiu: quem mostra o véu é a .ds-veu.
      className={`window noselect ds-veu${aberto}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && closeSuggestions()}
    >
      <div
        ref={janelaRef}
        className={`context suggestions ds-dialogo${aberto}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="suggestions-title"
      >
        <button
          type="button"
          className="close ds-resposta"
          aria-label={t('close', 'Fechar')}
          onClick={closeSuggestions}
        >
          <span className="material-symbols-rounded middle">close</span>
        </button>
        <h2 id="suggestions-title" className="nowrap" ref={tituloRef} tabIndex={-1}>
          {t('smart_assistant', 'Sugestões de buscas')}
        </h2>
        <p>{t('suggestions_smart_assistant', 'Selecione uma opção e inicie sua busca.')}</p>

        {query.isLoading ? (
          <div className="sk-stack" style={{ margin: '20px 0' }}>
            <LoadingStatus />
            {[90, 75, 82, 68].map((w) => (
              <Skeleton key={w} width={`${w}%`} height={16} />
            ))}
          </div>
        ) : suggestions && suggestions.length > 0 ? (
          <>
            <ul>
              {suggestions.map((s, i) => (
                <li key={i} role="button" tabIndex={0} onClick={() => apply(s)} onKeyDown={(e) => e.key === 'Enter' && apply(s)}>
                  {s.title}
                </li>
              ))}
            </ul>
            <div className="action center">
              <button type="button" className="button" onClick={() => query.refetch()}>
                {t('suggestions_more', 'Mais sugestões')}
              </button>
            </div>
          </>
        ) : (
          <p className="center">{t('suggestions_empty', 'Nenhuma sugestão disponível no momento.')}</p>
        )}
      </div>
    </div>,
    document.body,
  );
}
